import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, Clock, Copy, UserSquare2, Wrench } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import WeekHours from "@/components/WeekHours";
import { Button, Card, ErrorBanner, Input, Spinner } from "@/components/ui";
import { api, toApiError } from "@/lib/api";
import { diasAbertos, semanaDeDias } from "@/lib/horarios";
import { parseDecimal } from "@/lib/format";
import { useFormat, useT } from "@/lib/i18n";
import { useMe } from "@/lib/queries";

interface Onboarding {
  complete: boolean;
  unit: number | null;
  public_url: string;
  steps: Record<string, { done: boolean; count: number; linked?: number }>;
  suggestions: {
    week: { weekday: number; ranges: [string, string][] }[];
    services: { name: string; duration: number; price: string; buffer: number }[];
  };
}

export default function Onboarding() {
  const t = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();

  const { data, isLoading } = useQuery({
    queryKey: ["onboarding"],
    queryFn: async () => (await api.get<Onboarding>("/onboarding/")).data,
  });

  const [passo, setPasso] = useState(0);

  if (isLoading || !data || !me) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  const recarregar = () => {
    qc.invalidateQueries({ queryKey: ["onboarding"] });
    qc.invalidateQueries({ queryKey: ["services"] });
    qc.invalidateQueries({ queryKey: ["professionals"] });
  };

  const passos = [
    { titulo: t("setup.hours"), icone: Clock, done: data.steps.hours.done },
    { titulo: t("setup.services"), icone: Wrench, done: data.steps.services.done },
    { titulo: t("setup.team"), icone: UserSquare2, done: data.steps.professionals.done },
  ];

  return (
    <div className="min-h-dvh px-4 py-10">
      <div className="mx-auto max-w-lg">
        <p className="eyebrow mb-2">{t("setup.eyebrow")}</p>
        <h1 className="font-display text-3xl font-bold">
          {data.complete ? t("setup.allSet") : t("setup.title")}
        </h1>
        <p className="mt-2 text-steel-muted">
          {data.complete ? t("setup.doneTitle") : t("setup.intro")}
        </p>

        {/* trilha */}
        <div className="my-7 flex gap-2">
          {passos.map((p, i) => (
            <button
              key={p.titulo}
              onClick={() => setPasso(i)}
              className="flex-1 text-left"
              aria-current={passo === i}
            >
              <div
                className={`mb-2 h-1 rounded-full ${
                  p.done ? "bg-emerald-500" : passo === i ? "bg-nitro-500" : "bg-line"
                }`}
              />
              <span
                className={`flex items-center gap-1 text-xs ${
                  passo === i ? "text-steel" : "text-steel-dim"
                }`}
              >
                {p.done && <Check size={12} className="text-emerald-500" />}
                {p.titulo}
              </span>
            </button>
          ))}
        </div>

        {data.complete ? (
          <Pronto url={data.public_url} onEntrar={() => navigate("/")} />
        ) : (
          <Card className="p-6">
            {passo === 0 && (
              <PassoHorarios data={data} onDone={() => { recarregar(); setPasso(1); }} />
            )}
            {passo === 1 && (
              <PassoServicos data={data} onDone={() => { recarregar(); setPasso(2); }} />
            )}
            {passo === 2 && (
              <PassoProfissionais
                nomeDono={me.full_name}
                onDone={recarregar}
              />
            )}
          </Card>
        )}

        {!data.complete && (
          <p className="mt-5 text-center text-sm text-steel-dim">
            {t("setup.laterPrefix")}{" "}
            <button onClick={() => navigate("/")} className="text-racing-500 hover:underline">
              {t("setup.laterLink")}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- 1. horários */
function PassoHorarios({ data, onDone }: { data: Onboarding; onDone: () => void }) {
  const t = useT();
  const [semana, setSemana] = useState(() => semanaDeDias(data.suggestions.week));
  const abertos = diasAbertos(semana);

  const salvar = useMutation({
    mutationFn: async () =>
      api.put("/working-hours/", { unit: data.unit, days: abertos }),
    onSuccess: onDone,
  });

  const erro = salvar.isError ? toApiError(salvar.error) : null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-bold">{t("setup.whenOpen")}</h2>
        <p className="mt-1 text-sm text-steel-muted">{t("setup.hoursHint")}</p>
      </div>

      <WeekHours semana={semana} onChange={setSemana} />

      <ErrorBanner message={erro?.message} />

      <Button
        variant="racing"
        size="lg"
        className="w-full"
        disabled={abertos.length === 0 || salvar.isPending}
        onClick={() => salvar.mutate()}
      >
        {salvar.isPending ? (
          <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
        ) : (
          <>
            {t("setup.saveContinue")} <ArrowRight size={16} />
          </>
        )}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------- 2. serviços */
function PassoServicos({ data, onDone }: { data: Onboarding; onDone: () => void }) {
  const t = useT();
  const { money, decimalInput } = useFormat();
  /** Os três primeiros que o backend sugere, não uma lista escrita aqui.
   *
   *  Cravar os nomes em português fazia a oficina americana abrir com
   *  nenhum item marcado — o catálogo dela vem em inglês, e os nomes não
   *  batiam. Pegar do topo da sugestão funciona nos dois. */
  const [escolhidos, setEscolhidos] = useState<string[]>(() =>
    data.suggestions.services.slice(0, 3).map((s) => s.name),
  );
  /** Preço por serviço, só para os que o dono mexeu. O que não está aqui vai
   *  com o valor da sugestão — assim o campo em branco não vira R$ 0. */
  const [precos, setPrecos] = useState<Record<string, string>>({});
  /** O símbolo sai do próprio `Intl`, junto com a posição dele.
   *
   *  Cravar `currency === "BRL" ? "R$" : "$"` acertava o símbolo e errava
   *  o resto: o campo ao lado continuava escrevendo `89,00` para quem lê
   *  en-US, e a tela mostrava "$ 89,00" — meio dólar, meio real. */
  const simbolo = money(0).replace(/[\d.,\s]/g, "");

  const salvar = useMutation({
    mutationFn: async () =>
      api.post("/onboarding/services/", {
        services: escolhidos.map((name) =>
          precos[name] ? { name, price: parseDecimal(precos[name]) } : name,
        ),
      }),
    onSuccess: onDone,
  });

  const erro = salvar.isError ? toApiError(salvar.error) : null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-bold">{t("setup.whatOffer")}</h2>
        <p className="mt-1 text-sm text-steel-muted">{t("setup.servicesHint")}</p>
      </div>

      <div className="space-y-2">
        {data.suggestions.services.map((s) => {
          const marcado = escolhidos.includes(s.name);
          return (
            <label
              key={s.name}
              className={`flex cursor-pointer items-center gap-3 rounded-[4px] border p-3 transition ${
                marcado
                  ? "border-nitro-500 bg-nitro-500/10"
                  : "border-line hover:border-steel-dim"
              }`}
            >
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-nitro-500)]"
                checked={marcado}
                onChange={(e) =>
                  setEscolhidos((prev) =>
                    e.target.checked
                      ? [...prev, s.name]
                      : prev.filter((n) => n !== s.name),
                  )
                }
              />
              <span className="flex-1">
                <span className="block font-medium">{s.name}</span>
                <span className="block text-sm text-steel-muted">{s.duration} min</span>
              </span>

              {/* O campo só aparece quando o serviço está marcado: preço de
                  coisa que ele não vende é ruído na hora de decidir. O
                  onClick para de propagar porque o rótulo inteiro é o
                  checkbox — sem isso, digitar o preço desmarcaria o item. */}
              {marcado ? (
                <span
                  className="flex shrink-0 items-center gap-1.5"
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="text-sm text-steel-dim">{simbolo}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label={t("setup.priceOf", { servico: s.name })}
                    value={precos[s.name] ?? decimalInput(s.price)}
                    onChange={(e) =>
                      setPrecos((prev) => ({
                        ...prev,
                        [s.name]: e.target.value.replace(/[^0-9,.]/g, ""),
                      }))
                    }
                    className="tabular w-24 rounded-[2px] border border-line bg-carbon-900 px-2 py-1.5 text-right text-sm text-steel outline-none focus:border-nitro-500"
                  />
                </span>
              ) : (
                <span className="tabular shrink-0 text-sm text-steel-dim">
                  {money(s.price)}
                </span>
              )}
            </label>
          );
        })}
      </div>

      <ErrorBanner message={erro?.message} />

      <Button
        variant="racing"
        size="lg"
        className="w-full"
        disabled={escolhidos.length === 0 || salvar.isPending}
        onClick={() => salvar.mutate()}
      >
        {salvar.isPending ? (
          <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
        ) : (
          <>
            {t("setup.addServices", { n: escolhidos.length })} <ArrowRight size={16} />
          </>
        )}
      </Button>
    </div>
  );
}

/* --------------------------------------------------------- 3. profissionais */
function PassoProfissionais({
  nomeDono,
  onDone,
}: {
  nomeDono: string;
  onDone: () => void;
}) {
  const t = useT();
  // O dono geralmente É um dos profissionais — deixar o nome dele preenchido
  // economiza o passo mais comum.
  const [nome, setNome] = useState(nomeDono.split(" ")[0]);

  const { data: servicos } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services/")).data,
  });

  const criar = useMutation({
    mutationFn: async () =>
      api.post("/professionals/", {
        display_name: nome.trim(),
        commission_percent: "100",
        service_ids: (servicos ?? []).map((s: { id: number }) => s.id),
      }),
    onSuccess: onDone,
  });

  const erro = criar.isError ? toApiError(criar.error) : null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-bold">{t("setup.whoWorks")}</h2>
        <p
          className="mt-1 text-sm text-steel-muted"
          dangerouslySetInnerHTML={{ __html: t("setup.teamHint") }}
        />
      </div>

      <Input
        label={t("setup.displayName")}
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        placeholder={t("setup.namePlaceholder")}
      />

      <p className="rounded-[4px] border border-line bg-carbon-900 px-3 py-2.5 text-sm text-steel-muted">
        {t("setup.willRun", { n: servicos?.length ?? 0 })}
      </p>

      <ErrorBanner message={erro?.message} />

      <Button
        variant="racing"
        size="lg"
        className="w-full"
        disabled={nome.trim().length < 2 || criar.isPending}
        onClick={() => criar.mutate()}
      >
        {criar.isPending ? (
          <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
        ) : (
          t("setup.finish")
        )}
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ pronto */
function Pronto({ url, onEntrar }: { url: string; onEntrar: () => void }) {
  const t = useT();
  const [copiado, setCopiado] = useState(false);

  return (
    <Card className="border-emerald-800/50 bg-emerald-950/20 p-6 text-center">
      <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full border border-emerald-700 text-emerald-400">
        <Check size={24} />
      </div>
      <h2 className="font-display text-2xl font-bold">{t("setup.live")}</h2>
      <p className="mt-2 text-sm text-steel-muted">{t("setup.liveHint")}</p>

      <div className="mt-5 flex items-center gap-2 rounded-[4px] border border-line bg-carbon-900 p-2">
        <span className="flex-1 truncate px-2 text-left text-sm">{url}</span>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            navigator.clipboard?.writeText(url);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
          }}
        >
          <Copy size={14} />
          {t(copiado ? "setup.copied" : "setup.copy")}
        </Button>
      </div>

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Button
          variant="secondary"
          className="flex-1"
          onClick={() => window.open(url, "_blank")}
        >
          {t("setup.viewPage")}
        </Button>
        <Button variant="racing" className="flex-1" onClick={onEntrar}>
          {t("setup.goDashboard")}
        </Button>
      </div>
    </Card>
  );
}
